import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { recontactsApi, type RecontactsPage } from '../api/leads'
import {
  Button, Callout, Card, CardActions, DataTable, EmptyState, FilterBar, LoadingState, Page, PageHeader, Select,
  StatusPill, Tabs, type PillTone,
} from '../components/ui'

// F-049 (REQ-090-001): scheduled recontacts (GET /api/leads/recontacts). The one thing this view
// must make unmistakable is the difference the server draws: a recontact WITH a reason is a
// recycling request (a lost lead is reopened at that date, with a task and a notification);
// one without is only a reminder in the work queue.

const PAGE_SIZE = 25

const STATUS_LABELS: Record<string, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  converted: 'Convertito',
  lost: 'Perso',
}
const STATUS_TONES: Record<string, PillTone> = {
  new: 'primary',
  contacted: 'info',
  qualified: 'success',
  converted: 'neutral',
  lost: 'error',
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function RecontactKind({ status, reason }: { status: string; reason?: string | null | undefined }) {
  const hasReason = !!reason?.trim()
  if (!hasReason) {
    return (
      <div>
        <StatusPill tone="neutral">Solo promemoria</StatusPill>
        <div className="md-typescale-body-small text-muted">Il lead non verrà riaperto.</div>
      </div>
    )
  }
  if (status === 'lost') {
    return (
      <div>
        <StatusPill tone="success">Riaprirà il lead</StatusPill>
        <div className="md-typescale-body-small text-muted">Riapertura automatica alla data, con compito e notifica.</div>
      </div>
    )
  }
  return (
    <div>
      <StatusPill tone="warning">Riapre solo se perso</StatusPill>
      <div className="md-typescale-body-small text-muted">
        Il lead non è perso: verrà riaperto solo se viene perso prima di questa data.
      </div>
    </div>
  )
}

export function RecontactsPage() {
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<RecontactsPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await recontactsApi.list({ status, page, pageSize: PAGE_SIZE }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento dei ricontatti')
    } finally {
      setLoading(false)
    }
  }, [status, page])

  useEffect(() => {
    void load()
  }, [load])

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <Page>
      <PageHeader
        title="Ricontatti programmati"
        icon="event_repeat"
        tabs={
          <Tabs
            aria-label="Elenchi lead"
            value="recontacts"
            items={[
              { id: 'all', label: 'Tutti i lead', to: '/leads' },
              { id: 'recontacts', label: 'Ricontatti programmati', to: '/leads/recontacts' },
            ]}
          />
        }
      />

      <Callout tone="info" role="note">
        Ricontatti futuri, dal più vicino. Un ricontatto <strong>con motivo</strong> riapre
        automaticamente un lead perso alla data indicata (compito e notifica incluse);
        uno <strong>senza motivo</strong> è soltanto un promemoria nella coda di lavoro.
      </Callout>

      <FilterBar>
        <Select
          id="recontacts-status"
          label="Stato del lead"
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1) }}
        >
          <option value="">Tutti gli stati</option>
          <option value="lost">Perso (verrà riaperto)</option>
          <option value="new">Nuovo</option>
          <option value="contacted">Contattato</option>
          <option value="qualified">Qualificato</option>
        </Select>
        <div className="md-typescale-body-small text-muted align-self-center" aria-live="polite">
          {!loading && !error && `${total} ricontatti programmati`}
        </div>
      </FilterBar>

      {error && (
        <Callout tone="error" role="alert" action={
          <Button type="button" variant="text" onClick={() => void load()}>
            Riprova
          </Button>
        }>
          {error}
        </Callout>
      )}

      <Card variant="outlined" padding="none" aria-busy={loading}>
        {loading ? (
          <LoadingState label="Caricamento dei ricontatti..." />
        ) : error ? null : items.length === 0 ? (
          <EmptyState
            icon="event_available"
            title={`Nessun ricontatto programmato${status ? ' per questo stato' : ''}.`}
            description="Compaiono qui quando si registra una chiamata con una data di richiamo futura."
          />
        ) : (
          <>
            <DataTable variant="plain" caption="Ricontatti programmati dei lead">
              <thead>
                <tr>
                  <th scope="col">Lead</th>
                  <th scope="col">Stato</th>
                  <th scope="col">Ricontatto</th>
                  <th scope="col">Motivo</th>
                  <th scope="col">Cosa succede</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.leadId}>
                    <td>
                      <Link to={`/leads/${r.leadId}`} className="text-decoration-none md-typescale-emphasized-body-medium">
                        {r.leadName}
                      </Link>
                    </td>
                    <td>
                      <StatusPill tone={STATUS_TONES[r.status] ?? 'neutral'}>
                        {STATUS_LABELS[r.status] ?? r.status}
                      </StatusPill>
                    </td>
                    <td className="text-nowrap">
                      <time dateTime={r.recontactAt}>{fmtDateTime(r.recontactAt)}</time>
                    </td>
                    <td>{r.reason?.trim() ? r.reason : <span className="text-muted">Nessun motivo</span>}</td>
                    <td><RecontactKind status={r.status} reason={r.reason} /></td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {totalPages > 1 && (
              <CardActions align="end">
                <Button type="button" variant="outlined" size="sm"
                  disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  Precedente
                </Button>
                <span className="md-typescale-body-small text-muted">Pagina {page} di {totalPages}</span>
                <Button type="button" variant="outlined" size="sm"
                  disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  Successiva
                </Button>
              </CardActions>
            )}
          </>
        )}
      </Card>
    </Page>
  )
}
